import type {
  CoverageRestrictionsT,
  FormularyResponseT,
  PayerAlternativeT,
  PayerCopayT,
  TherapeuticAlternativesT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import type { RxMedication } from '@/entities/rx';

/**
 * Single source of truth for the F1–F7 cert-matrix display rules.
 *
 * The DoseSpot ``/formulary`` endpoint is a near-passthrough — it
 * returns whatever the payer / PBM published, without applying the
 * "is alternative display permitted?", "must therapeutics be hidden if
 * payer alts exist?", or "is the copay even valid for this status?"
 * rules. Those rules are codified here so every consumer (Details
 * badge, Review panel, Alternatives modal) renders the same decision.
 *
 * References — all numbered cases from the cert spreadsheet:
 *
 * - **F1**: ON-FORMULARY/PREFERRED — alternatives optional.
 * - **F2 / F6A**: UNKNOWN status — alternatives **forbidden**.
 * - **F3A**: Payer ALT file present → therapeutics hidden by default.
 * - **F3E**: Multi-ingredient target → only multi-ingredient alts.
 * - **F3G**: Obsolete drug → alternatives **forbidden**.
 * - **F3J**: NOT REIMBURSABLE + status code "0" → copay hidden.
 * - **F4F / F4D**: Multiple coverage messages and/or quantity limits
 *   must each be displayed (no truncation).
 * - **F.108**: When an alternative carries a Retail copay, the Retail
 *   copay must be displayed; Specialty copays are optional. The
 *   ``pickAlternativeRetailCopay`` helper enforces the precedence
 *   Retail > Any > MailOrder > LongTermCare > everything-else and
 *   skips Specialty when no retail-equivalent factor is published.
 */

/** Buckets for grouping the wide range of payer-published statuses. */
export type FormularyStatusBucket =
  | 'on-formulary-preferred'
  | 'on-formulary-not-preferred'
  | 'non-formulary'
  | 'not-reimbursable'
  | 'excluded'
  | 'unknown';

export interface FormularyCoverageMessage {
  title: string;
  description: string;
  tone: 'positive' | 'warning' | 'danger' | 'neutral';
}

/** Numeric rank used for "alternative status > target status" filters. */
const STATUS_RANK: Record<FormularyStatusBucket, number> = {
  'not-reimbursable': 0,
  excluded: 0,
  'non-formulary': 1,
  'on-formulary-not-preferred': 2,
  'on-formulary-preferred': 3,
  unknown: -1,
};

/**
 * DoseSpot's published Formulary Status Key — maps the raw numeric
 * code that comes back in ``FormularyStatus`` (and elsewhere) to the
 * human label and the visual bucket. The numeric channel is the
 * authoritative source: when ``FormularyStatus`` is ``"3"`` the alt
 * is always Preferred Level 1 regardless of whether
 * ``FormularyStatusText`` is populated.
 *
 *   U  = Unknown
 *   0  = Not Reimbursable
 *   1  = Non-Formulary
 *   2  = On Formulary, Not Preferred
 *   3  = On Formulary, Preferred Level 1
 *   4  = On Formulary, Preferred Level 2
 *   5  = On Formulary, Preferred Level 3
 *   6  = On Formulary, Preferred Level 4
 *   7  = On Formulary, Preferred Level 5
 *   8  = On Formulary, Preferred Level 6
 *   9  = On Formulary, Preferred Level 7
 *   10 = On Formulary, Preferred Level 8
 */
interface FormularyCodeEntry {
  label: string;
  bucket: FormularyStatusBucket;
}

const FORMULARY_STATUS_CODES: Record<string, FormularyCodeEntry> = {
  U: { label: 'Unknown', bucket: 'unknown' },
  '0': { label: 'Not Reimbursable', bucket: 'not-reimbursable' },
  '1': { label: 'Non-Formulary', bucket: 'non-formulary' },
  '2': { label: 'On Formulary, Not Preferred', bucket: 'on-formulary-not-preferred' },
  '3': { label: 'On Formulary, Preferred Level 1', bucket: 'on-formulary-preferred' },
  '4': { label: 'On Formulary, Preferred Level 2', bucket: 'on-formulary-preferred' },
  '5': { label: 'On Formulary, Preferred Level 3', bucket: 'on-formulary-preferred' },
  '6': { label: 'On Formulary, Preferred Level 4', bucket: 'on-formulary-preferred' },
  '7': { label: 'On Formulary, Preferred Level 5', bucket: 'on-formulary-preferred' },
  '8': { label: 'On Formulary, Preferred Level 6', bucket: 'on-formulary-preferred' },
  '9': { label: 'On Formulary, Preferred Level 7', bucket: 'on-formulary-preferred' },
  '10': { label: 'On Formulary, Preferred Level 8', bucket: 'on-formulary-preferred' },
};

function lookupCodeEntry(value: string | null | undefined): FormularyCodeEntry | null {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const upper = trimmed.toUpperCase();
  return FORMULARY_STATUS_CODES[upper] ?? null;
}

/**
 * Human label for a raw numeric ``FormularyStatus`` code. Returns
 * null when the code is unrecognized (a payer extension we don't
 * model) so callers can decide whether to fall back to "Code N" or
 * just hide the label.
 */
export function formularyStatusLabel(code: string | null | undefined): string | null {
  return lookupCodeEntry(code)?.label ?? null;
}

/**
 * Map a free-text formulary status string + restrictions slice into a
 * bucket. Liberal matching: real DoseSpot returns wide variation in
 * casing and phrasing.
 *
 * The ``restrictions.IsExcluded`` flag short-circuits to ``excluded``
 * regardless of the textual status — per F3F (drug exclusion factor
 * overrides a listed status of 2 → NOT REIMBURSABLE/EXCLUDED bucket).
 */
export function bucketFormularyStatus(
  status: string | null | undefined,
  restrictions: CoverageRestrictionsT | null | undefined
): FormularyStatusBucket {
  if (restrictions?.IsExcluded) {
    return 'excluded';
  }
  // Fast path: when the status is the numeric code from DoseSpot's
  // published Formulary Status Key (or the literal "U") we can look
  // it up directly without any heuristic text matching.
  const codeEntry = lookupCodeEntry(status);
  if (codeEntry) {
    return codeEntry.bucket;
  }

  const lower = (status ?? '').toLowerCase().trim();
  if (lower === '') {
    return 'unknown';
  }
  // "reimburs" prefix covers both "Not Reimbursable" / "not-reimbursable"
  // (the cert spec wording) and "Non-Reimbursable" / "non reimbursable"
  // (what real DoseSpot responses sometimes carry — see the F3J cert
  // case JSON). We test the broad ``reimburs`` substring so this single
  // arm handles both prefixes without later branches accidentally
  // catching the ``non-`` variant as ``non-formulary``.
  if (lower.includes('reimburs')) {
    return 'not-reimbursable';
  }
  if (lower.includes('exclud')) {
    return 'excluded';
  }
  if (lower.includes('non-formulary') || lower.includes('non formulary')) {
    return 'non-formulary';
  }
  if (lower.includes('not preferred') || lower.includes('not-preferred')) {
    return 'on-formulary-not-preferred';
  }
  if (
    lower.includes('preferred') ||
    lower.includes('on formulary') ||
    lower.includes('on-formulary')
  ) {
    return 'on-formulary-preferred';
  }
  return 'unknown';
}

/**
 * Translate the plan status and restriction flags into one consistent,
 * clinician-facing coverage summary. Restriction flags take precedence over
 * positive formulary wording so we never describe a restricted medication as
 * unrestricted.
 */
export function describeFormularyCoverage(
  status: string | null | undefined,
  restrictions: CoverageRestrictionsT | null | undefined
): FormularyCoverageMessage {
  const bucket = bucketFormularyStatus(status, restrictions);

  if (restrictions?.IsExcluded === true || bucket === 'excluded') {
    return {
      title: 'May not be covered',
      description:
        'The patient’s plan may exclude this medication. Review plan alternatives before continuing.',
      tone: 'danger',
    };
  }

  if (restrictions?.HasPriorAuthorization === true) {
    return {
      title: 'Insurance approval required',
      description:
        'The prescription can be prepared now, but the plan must approve it before the pharmacy can fill it.',
      tone: 'warning',
    };
  }

  if (bucket === 'not-reimbursable' || bucket === 'non-formulary') {
    return {
      title: 'May not be covered',
      description: 'The patient’s plan may not pay for this medication.',
      tone: 'danger',
    };
  }

  if (bucket === 'unknown') {
    return {
      title: 'Coverage not confirmed',
      description: 'The patient’s plan did not return a clear coverage decision.',
      tone: 'neutral',
    };
  }

  const requirementLabels = collectPlanRequirementLabels(restrictions);
  if (requirementLabels.length > 0) {
    const preference =
      bucket === 'on-formulary-preferred' ? 'This medication is preferred by the plan. ' : '';
    return {
      title: 'Plan requirements apply',
      description: `${preference}Review ${formatNaturalList(requirementLabels)} before continuing.`,
      tone: 'warning',
    };
  }

  if (bucket === 'on-formulary-not-preferred') {
    return {
      title: 'Covered, but not preferred',
      description: 'The plan lists other medications it may prefer or cover at a lower cost.',
      tone: 'warning',
    };
  }

  return {
    title: 'Preferred by the patient’s plan',
    description: 'No formulary restriction was identified for this medication.',
    tone: 'positive',
  };
}

function collectPlanRequirementLabels(
  restrictions: CoverageRestrictionsT | null | undefined
): string[] {
  if (!restrictions) {
    return [];
  }

  const labels: string[] = [];
  if (restrictions.HasStepTherapy) {
    labels.push('step therapy');
  }
  if (restrictions.HasQuantityLimit) {
    labels.push('the quantity limit');
  }
  if (restrictions.HasAgeLimit) {
    labels.push('the age limit');
  }
  if (restrictions.HasMedicalNecessity) {
    labels.push('the medical-necessity requirement');
  }
  if (restrictions.GenderRestriction != null && restrictions.GenderRestriction !== 'Unknown') {
    labels.push('the patient eligibility restriction');
  }
  return labels;
}

function formatNaturalList(values: string[]): string {
  if (values.length <= 1) {
    return values[0] ?? '';
  }
  if (values.length === 2) {
    return `${values[0]} and ${values[1]}`;
  }
  return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`;
}

/** True if the bucket should suppress copay rendering (cert F3J). */
export function shouldHideCopayForBucket(bucket: FormularyStatusBucket): boolean {
  return bucket === 'unknown' || bucket === 'not-reimbursable';
}

/**
 * Heuristic: a medication is "multi-ingredient" if its ``DrugName``
 * contains a hyphen segment (e.g. "LISINOPRIL-HCTZ") or its display
 * name explicitly contains an ingredient list with ``and`` /
 * combination markers. We use the dispensable drug's ``GenericDrugName``
 * first (typically the most reliable), then ``DrugName``.
 *
 * DoseSpot does not expose a structured "multi-ingredient" flag on
 * ``MedicationSelectResultT``, so this is conservative — false
 * positives are tolerable since the alt filter only excludes
 * single-ingredient alternatives, never the target itself.
 */
export function isMultiIngredient(medication: RxMedication | null | undefined): boolean {
  if (!medication) {
    return false;
  }
  const generic = medication.GenericDrugName ?? '';
  const display = medication.DisplayName ?? medication.DrugName ?? '';
  return matchesMultiIngredient(generic) || matchesMultiIngredient(display);
}

function matchesMultiIngredient(text: string): boolean {
  if (text.trim() === '') {
    return false;
  }
  // Common DoseSpot multi-ingredient markers: "FOO-BAR" (compound
  // generic), "and", explicit "/" separator (less common), or the
  // textual marker "** Multi-Ingredient Drug **" the cert matrix uses.
  const lower = text.toLowerCase();
  if (lower.includes('multi-ingredient')) {
    return true;
  }
  // Hyphenated generic with two distinct tokens, e.g. "LISINOPRIL-HCTZ".
  // Skip dosage hyphenations like "10-12.5" by requiring alpha tokens
  // on both sides.
  const hyphenated = /[a-z]{3,}-[a-z]{3,}/i;
  if (hyphenated.test(text)) {
    return true;
  }
  return false;
}

/** Display rule for therapeutic alternatives (cert §6 / §7). */
export interface AlternativesPolicy {
  showPayerAlternatives: boolean;
  showTherapeuticAlternatives: boolean;
  /** When non-null, an explanatory string the UI may surface. */
  notice: string | null;
}

/**
 * Decide whether each alternatives section should render at all,
 * given the target medication, its formulary bucket, and what the
 * formulary response carries.
 *
 * - Unknown / Obsolete target → both forbidden (F2, F3G, F6A).
 * - Payer ALT file present + therapeutic alts also returned → hide
 *   therapeutics (F3A default policy; EX-00238 precedence override
 *   not modelled — opt-in flag could be added later).
 * - Otherwise both sections render if their list is non-empty.
 */
export function decideAlternativesPolicy(args: {
  medication: RxMedication | null | undefined;
  bucket: FormularyStatusBucket;
  payerAlternatives: PayerAlternativeT[] | null | undefined;
  therapeuticAlternatives: TherapeuticAlternativesT[] | null | undefined;
}): AlternativesPolicy {
  const { medication, bucket, payerAlternatives, therapeuticAlternatives } = args;
  const hasPayer = (payerAlternatives?.length ?? 0) > 0;
  const hasTherapeutic = (therapeuticAlternatives?.length ?? 0) > 0;

  if (medication?.IsObsolete) {
    return {
      showPayerAlternatives: false,
      showTherapeuticAlternatives: false,
      notice: 'This medication is no longer available, so alternatives cannot be shown.',
    };
  }
  if (bucket === 'unknown') {
    return {
      showPayerAlternatives: false,
      showTherapeuticAlternatives: false,
      notice: 'Insurance coverage is not confirmed, so medication options cannot be shown.',
    };
  }
  if (hasPayer && hasTherapeutic) {
    return {
      showPayerAlternatives: true,
      showTherapeuticAlternatives: false,
      notice: null,
    };
  }
  return {
    showPayerAlternatives: hasPayer,
    showTherapeuticAlternatives: hasTherapeutic,
    notice: null,
  };
}

/**
 * Sort payer alternatives. The cert matrix is explicit: payer-defined
 * alts must be ordered by the payer's stated **preference level**, NOT
 * by formulary status. Some DoseSpot responses carry the preference
 * level in ``RepresentativeFormularyCoverage.OrderRank`` /
 * ``FormularyInfo.OrderRank``; when absent we preserve the original
 * order.
 */
export function sortPayerAlternatives(alts: PayerAlternativeT[]): PayerAlternativeT[] {
  return [...alts].sort((a, b) => preferenceRank(a) - preferenceRank(b));
}

function preferenceRank(alt: PayerAlternativeT): number {
  // DoseSpot does not expose a preference level on the public swagger
  // for `PayerAlternativeAPI`; we treat the wire order as already
  // sorted ("PAYER ALTERNATIVES (IN ORDER)" per the cert) and only
  // promote items with a known higher status if the wire ordering
  // happens to disagree with status.
  const status =
    alt.RepresentativeFormularyCoverage?.FormularyStatusText ??
    alt.RepresentativeFormularyCoverage?.FormularyStatus ??
    null;
  const bucket = bucketFormularyStatus(status, null);
  return -STATUS_RANK[bucket];
}

/**
 * Filter and sort therapeutic alternatives.
 *
 * Per F3E: when target is multi-ingredient, the list must contain
 * only multi-ingredient drugs. Per F3K: only commercially-available
 * drugs may appear (we proxy this via the ``FormularyInfo.IsRx`` /
 * non-zero status check — DoseSpot scrubs market-availability before
 * returning, but we drop entries with explicit status "0" / no
 * formulary info to be safe).
 *
 * Sort order for therapeutics: highest formulary status first
 * (preferred → on-formulary → non-formulary), original order
 * preserved within a status bucket.
 */
export function filterAndSortTherapeuticAlternatives(args: {
  medication: RxMedication | null | undefined;
  alternatives: TherapeuticAlternativesT[];
}): TherapeuticAlternativesT[] {
  const { medication, alternatives } = args;
  const targetIsMulti = isMultiIngredient(medication);
  const filtered = alternatives.filter((alt) => {
    if (targetIsMulti) {
      const altText = alt.FullDisplayString ?? alt.DisplayName ?? '';
      return matchesMultiIngredient(altText);
    }
    return true;
  });
  return [...filtered].sort((a, b) => therapeuticRank(b) - therapeuticRank(a));
}

function therapeuticRank(alt: TherapeuticAlternativesT): number {
  const status = alt.FormularyInfo?.FormularyStatusMessage ?? alt.FormularyStatus ?? null;
  const bucket = bucketFormularyStatus(status, null);
  return STATUS_RANK[bucket];
}

/** Format a copay amount with an explicit dollar prefix. */
export function formatDollar(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) {
    return null;
  }
  return `$${value.toFixed(2).replace(/\.00$/, '')}`;
}

/** Format a percent copay with a trailing "%" sign.
 *  DoseSpot returns the value as a decimal fraction (e.g. 0.2 = 20%). */
export function formatPercent(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) {
    return null;
  }
  const pct = value * 100;
  const display = Number.isInteger(pct)
    ? String(pct)
    : pct.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return `${display}%`;
}

/**
 * Compose a single human-readable line for one ``PayerCopayT`` entry —
 * combines flat + percent + min/max + tier + days-supply per the
 * cert's expected format ("DRUG SPECIFIC: TIER=2/3, FLAT $30, 30 DAYS,
 * PHARMACY TYPE = ANY").
 *
 * Returns ``null`` when the entry has no rendered fields (callers can
 * skip empty rows).
 */
export function describeCopay(copay: PayerCopayT): string | null {
  const parts: string[] = [];
  if (copay.CopayTier != null) {
    const tier = copay.CopayTier;
    const max = copay.MaximumCopayTier;
    parts.push(max != null ? `Tier ${tier}/${max}` : `Tier ${tier}`);
  }
  const flat = formatDollar(copay.FlatCopayAmount ?? null);
  const percent = formatPercent(copay.PercentCopayAmount ?? null);
  if (flat != null && percent != null) {
    const first = copay.FlatCopayAmountFirst === true;
    parts.push(first ? `${flat} + ${percent}` : `${percent} + ${flat}`);
  } else if (flat != null) {
    parts.push(flat);
  } else if (percent != null) {
    parts.push(percent);
  }
  const min = formatDollar(copay.MinimumCopayAmount ?? null);
  const max = formatDollar(copay.MaximumCopayAmount ?? null);
  if (min != null || max != null) {
    parts.push(`min ${min ?? '—'} / max ${max ?? '—'}`);
  }
  if (copay.DaysSupply != null) {
    parts.push(`${copay.DaysSupply} days`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Pick the single copay factor that should represent an alternative
 * row in the alts list (Details badge / Review panel / Alternatives
 * modal). Cert F.108 requires the Retail copay to appear whenever the
 * alternative carries one; Specialty copays are NOT required to be
 * shown on alts (they're shown on the target drug's own benefit
 * coordination panel, which has more vertical space).
 *
 * Precedence (highest first):
 *   1. Retail
 *   2. Any (covers all pharmacy types — effectively retail too)
 *   3. MailOrder / LongTermCarePharmacy / TwentyFourHourPharmacy
 *   4. Other (FaxPharmacy, EPCS)
 *
 * Specialty is intentionally excluded — if the only published copay is
 * Specialty, this returns null and the alt row simply omits the copay
 * line. Drug-specific factors (``IsDrugSpecific``) win over generic
 * factors at the same precedence level.
 */
const ALT_COPAY_PHARMACY_RANK: Record<string, number> = {
  Retail: 0,
  Any: 1,
  MailOrder: 2,
  LongTermCarePharmacy: 3,
  TwentyFourHourPharmacy: 3,
  FaxPharmacy: 4,
  EPCS: 4,
};

export function pickAlternativeRetailCopay(
  factors: PayerCopayT[] | null | undefined
): PayerCopayT | null {
  if (!factors || factors.length === 0) {
    return null;
  }
  let best: PayerCopayT | null = null;
  let bestRank = Number.POSITIVE_INFINITY;
  let bestSpecificity = -1;
  for (const factor of factors) {
    const type = factor.PharmacyType ?? 'Any';
    if (type === 'SpecialtyPharmacy') {
      continue;
    }
    const rank = ALT_COPAY_PHARMACY_RANK[type] ?? Number.POSITIVE_INFINITY;
    const specificity = factor.IsDrugSpecific === true ? 1 : 0;
    if (rank < bestRank || (rank === bestRank && specificity > bestSpecificity)) {
      best = factor;
      bestRank = rank;
      bestSpecificity = specificity;
    }
  }
  return best;
}

/** Human label for a `PayerCopay.PharmacyType` value, or "Any" for null. */
export function pharmacyTypeLabel(type: PayerCopayT['PharmacyType']): string {
  if (type == null) {
    return 'Any';
  }
  switch (type) {
    case 'Retail':
      return 'Retail';
    case 'MailOrder':
      return 'Mail';
    case 'LongTermCarePharmacy':
      return 'LTC';
    case 'SpecialtyPharmacy':
      return 'Specialty';
    case 'TwentyFourHourPharmacy':
      return '24-Hour';
    case 'EPCS':
      return 'EPCS';
    case 'FaxPharmacy':
      return 'Fax';
    case 'Any':
    default:
      return 'Any';
  }
}

/**
 * Assemble the master display state for a formulary response. Takes
 * the raw response + the target medication, returns a normalized view
 * consumed by the panel/dialog/badge so each consumer doesn't reapply
 * the same policy logic.
 */
export interface FormularyDisplayState {
  bucket: FormularyStatusBucket;
  hideCopay: boolean;
  policy: AlternativesPolicy;
  payerAlternatives: PayerAlternativeT[];
  therapeuticAlternatives: TherapeuticAlternativesT[];
}

export function deriveFormularyDisplayState(args: {
  formulary: FormularyResponseT | null | undefined;
  medication: RxMedication | null | undefined;
}): FormularyDisplayState {
  const { formulary, medication } = args;
  const status = formulary?.FormularyStatus ?? null;
  const restrictions = formulary?.Restrictions ?? null;
  const bucket = bucketFormularyStatus(status, restrictions);
  const hideCopay = shouldHideCopayForBucket(bucket);

  const rawPayerAlts = formulary?.Alternatives ?? null;
  const rawTherapeutic = formulary?.TherapeuticAlternatives ?? null;
  const policy = decideAlternativesPolicy({
    medication,
    bucket,
    payerAlternatives: rawPayerAlts,
    therapeuticAlternatives: rawTherapeutic,
  });

  const payerAlternatives =
    policy.showPayerAlternatives && rawPayerAlts ? sortPayerAlternatives(rawPayerAlts) : [];
  const therapeuticAlternatives =
    policy.showTherapeuticAlternatives && rawTherapeutic
      ? filterAndSortTherapeuticAlternatives({ medication, alternatives: rawTherapeutic })
      : [];

  return {
    bucket,
    hideCopay,
    policy,
    payerAlternatives,
    therapeuticAlternatives,
  };
}
